import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Edit2, Trash2, Key, Search, UserCheck, UserX, ArrowLeft, Copy, Check, Eye, EyeOff, HardDrive, Database, Book, Image, Headphones, Circle, Clock, RefreshCw, Wifi, WifiOff, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Loader2, CalendarClock, X as XIcon, Mail, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import UserAvatar from '../components/avatars/UserAvatar';
import ValidityEditorModal from '../components/ValidityEditorModal';
import BulkValidityModal from '../components/BulkValidityModal';
import AssignBooksModal from '../components/AssignBooksModal';
import ActivityMembersDialog from '../components/admin/ActivityMembersDialog';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const USERS_PER_PAGE = 15;

// Real-time relative time formatter with second-level precision
const formatActivityTime = (activityStatus, lastActive, secondsInactive) => {
  if (!lastActive && !activityStatus) return 'Never active';
  if (activityStatus === 'Online') return 'Online now';
  return activityStatus || 'Offline';
};

const UserManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [activityGroup, setActivityGroup] = useState(null); // 'online' | 'recent' | 'offline'
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [storage, setStorage] = useState(null);
  const [showStorage, setShowStorage] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = useRef(null);
  const [tick, setTick] = useState(0);
  // ===== Validity / 7-day-trial admin controls (2026-05) =====
  const [validityModalUser, setValidityModalUser] = useState(null);
  const [showBulkValidity, setShowBulkValidity] = useState(false);
  // ===== Manual book → author assignment (2026-06-08) =====
  const [assignBooksUser, setAssignBooksUser] = useState(null);
  // Sort by activity: online → recently active → offline (2026-06)
  const [sortByActivity, setSortByActivity] = useState(false);
  // Activity filter chips: null | 'online' | 'recent' | 'offline' (2026-06)
  const [activityFilter, setActivityFilter] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member',
    status: 'active',
    notes: ''
  });
  const [emailValidation, setEmailValidation] = useState({ status: null, message: '' }); // null, 'checking', 'valid', 'invalid'
  const emailCheckTimer = useRef(null);

  // Password policy checks
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { checks: [], allPassed: false };
    const checks = [
      { label: 'At least 8 characters', passed: pwd.length >= 8 },
      { label: 'Uppercase letter', passed: /[A-Z]/.test(pwd) },
      { label: 'Lowercase letter', passed: /[a-z]/.test(pwd) },
      { label: 'Number', passed: /\d/.test(pwd) },
      { label: 'Special character', passed: /[!@#$%^&*()\-_+=[\]{}|;':",./<>?`~\\]/.test(pwd) },
    ];
    return { checks, allPassed: checks.every(c => c.passed) };
  };

  // Live email validation (debounced)
  const validateEmailLive = (email) => {
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
    if (!email || !email.includes('@')) {
      setEmailValidation({ status: null, message: '' });
      return;
    }
    setEmailValidation({ status: 'checking', message: 'Validating email...' });
    emailCheckTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.post(`${API}/admin/validate-email`, { email }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.data.valid) {
          setEmailValidation({ status: 'valid', message: 'Email is valid' });
        } else {
          setEmailValidation({ status: 'invalid', message: resp.data.reason || 'Invalid email' });
        }
      } catch {
        setEmailValidation({ status: null, message: '' });
      }
    }, 800);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Auto-refresh every 10 seconds for real-time tracking
  useEffect(() => {
    if (!autoRefresh) {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      return;
    }
    refreshIntervalRef.current = setInterval(() => {
      fetchUsersSilent();
      setTick(t => t + 1);
    }, 10000);
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [autoRefresh]);

  // Tick every second to update relative times in the UI
  useEffect(() => {
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickInterval);
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users-with-activity`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setUsers(Array.isArray(response.data) ? response.data : (response.data?.users || []));
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching users:', error);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(Array.isArray(response.data) ? response.data : (response.data?.users || []));
      } catch (fallbackError) {
        toast({
          title: 'User list is responding slowly',
          description: 'The server took too long. Retrying automatically — live activity statuses may be delayed.',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh - no loading state change
  const fetchUsersSilent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users-with-activity`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      setUsers(Array.isArray(response.data) ? response.data : (response.data?.users || []));
      setLastRefresh(new Date());
    } catch (error) {
      // Auto-refresh silent fail — log to console so devtools can show it but
      // don't toast (auto-refresh would otherwise spam the UI on every blip).
      console.warn('[UserManagement] auto-refresh failed:', error?.message || error);
    }
  };

  // Compute real-time relative time from last_active using the tick counter
  const getRealtimeStatus = useCallback((user) => {
    // Pending approvals are not "active" members yet — own group, never Online/Recent.
    if (user.status === 'pending_admin_approval') return { text: 'Pending approval', color: 'fuchsia' };
    // Only created_at known (never heartbeat/login/read) → not activity at all.
    if (user.activity_source === 'created' || (!user.last_seen && !user.last_login && !user.last_active)) {
      return { text: 'Never active', color: 'gray' };
    }
    if (user.activity_status === 'Online' || user.is_online) return { text: 'Online now', color: 'green' };
    
    if (user.last_active) {
      const lastActiveDate = new Date(user.last_active);
      const now = new Date();
      const diffMs = now - lastActiveDate;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);

      if (diffSec <= 30) return { text: 'Online now', color: 'green' };
      if (diffSec < 60) return { text: `${diffSec}s ago`, color: 'yellow' };
      if (diffMin < 2) return { text: `${diffMin}m ${diffSec % 60}s ago`, color: 'yellow' };
      if (diffMin < 60) return { text: `${diffMin}m ago`, color: 'orange' };
      if (diffHour < 24) {
        const remMin = diffMin % 60;
        return { text: remMin > 0 ? `${diffHour}h ${remMin}m ago` : `${diffHour}h ago`, color: 'gray' };
      }
      if (diffDay === 1) return { text: 'Yesterday', color: 'gray' };
      if (diffDay < 7) return { text: `${diffDay}d ago`, color: 'gray' };
      return { text: `${diffDay}d ago`, color: 'gray' };
    }
    
    return { text: 'Never active', color: 'gray' };
  }, [tick]);

  const fetchStorage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorage(response.data);
      setShowStorage(true);
    } catch (error) {
      console.error('Error fetching storage:', error);
      toast({
        title: 'Error',
        description: 'Failed to load storage info',
        variant: 'destructive'
      });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'User Created',
        description: `${formData.name} has been added successfully.`
      });
      
      // Show temporary password if generated
      if (response.data.temporary_password) {
        setTempPassword(response.data.temporary_password);
      } else {
        setShowCreateModal(false);
        resetForm();
      }
      
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create user',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const updateData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;
      if (formData.role) updateData.role = formData.role;
      if (formData.status) updateData.status = formData.status;
      updateData.notes = formData.notes;
      
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'User Updated',
        description: 'User information has been updated.'
      });
      
      setShowEditModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'User Deleted',
        description: `${userName} has been removed.`
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTempPassword(response.data.temporary_password);
      setSelectedUser({ name: userName });
      
      toast({
        title: 'Password Reset',
        description: 'A new temporary password has been generated.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reset password',
        variant: 'destructive'
      });
    }
  };

  const handleResendVerification = async (userId, userName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Verification Email Sent',
        description: `A new verification email has been sent to ${userName}.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resend verification',
        variant: 'destructive'
      });
    }
  };

  const handleManualActivate = async (userId, userName, currentStatus) => {
    const isApproval = currentStatus === 'pending_admin_approval';
    const confirmMsg = isApproval
      ? `Approve and activate ${userName}? They'll receive a confirmation email and can sign in immediately.`
      : `Manually activate ${userName} without email verification?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'User Activated',
        description: `${userName} has been activated.`
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to activate user',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'member',
      status: 'active',
      notes: ''
    });
    setSelectedUser(null);
    setTempPassword(null);
    setCopiedPassword(false);
    setEmailValidation({ status: null, message: '' });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      status: user.status || 'active',
      notes: user.notes || ''
    });
    setShowEditModal(true);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const activityRank = useCallback((user) => {
    const color = getRealtimeStatus(user).color;
    if (color === 'fuchsia') return -1; // pending approval first — needs admin action
    if (color === 'green') return 0;
    if (color === 'yellow') return 1;
    if (color === 'orange') return 2;
    return 3;
  }, [getRealtimeStatus]);

  const matchesActivityFilter = useCallback((user) => {
    if (!activityFilter) return true;
    if (activityFilter === 'pending') return user.status === 'pending_admin_approval';
    const color = getRealtimeStatus(user).color;
    if (activityFilter === 'online') return color === 'green';
    if (activityFilter === 'recent') return color === 'yellow' || color === 'orange';
    return color === 'gray';
  }, [activityFilter, getRealtimeStatus]);

  const filteredUsers = useMemo(() => {
    const list = users.filter(user =>
      (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      matchesActivityFilter(user)
    );
    if (!sortByActivity) return list;
    return [...list].sort((a, b) => {
      const rankDiff = activityRank(a) - activityRank(b);
      if (rankDiff !== 0) return rankDiff;
      if (activityRank(a) === -1) {
        // pending approvals: newest sign-up first
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      }
      const aTime = a.last_active ? new Date(a.last_active).getTime() : 0;
      const bTime = b.last_active ? new Date(b.last_active).getTime() : 0;
      return bTime - aTime;
    });
  }, [users, searchTerm, sortByActivity, activityRank, matchesActivityFilter]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const userRole = localStorage.getItem('userRole');
  if (userRole !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                <Users className="h-8 w-8 mr-3 text-red-500" />
                User Management
              </h1>
              <div className="flex items-center space-x-3 mt-1">
                <p className="text-gray-400">
                  {filteredUsers.length} users{totalPages > 1 ? ` · Page ${currentPage} of ${totalPages}` : ''}
                </p>
                {autoRefresh && (
                  <span className="flex items-center space-x-1.5 text-xs px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-green-400">LIVE</span>
                  </span>
                )}
                {lastRefresh && (
                  <span className="text-xs text-gray-500">
                    Updated {lastRefresh.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              variant="outline"
              size="sm"
              className={`border-white/20 text-white hover:bg-white/10 ${autoRefresh ? 'bg-green-500/10 border-green-500/30' : ''}`}
              data-testid="toggle-auto-refresh"
              title={autoRefresh ? 'Auto-refresh ON (every 10s)' : 'Auto-refresh OFF'}
            >
              {autoRefresh ? <Wifi className="h-4 w-4 mr-1 text-green-400" /> : <WifiOff className="h-4 w-4 mr-1" />}
              {autoRefresh ? 'Live' : 'Paused'}
            </Button>
            <Button
              onClick={() => { fetchUsers(); }}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-users-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              onClick={fetchStorage}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="storage-btn"
            >
              <HardDrive className="h-4 w-4 mr-2" />
              Storage
            </Button>
            <Button
              onClick={() => setShowBulkValidity(true)}
              variant="outline"
              className="border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/10"
              data-testid="bulk-validity-btn"
            >
              <CalendarClock className="h-4 w-4 mr-2" />
              Bulk validity
            </Button>
            <Button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="add-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Online/Offline Summary — click a card to see who's in the group */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <button
            type="button"
            onClick={() => setActivityGroup('online')}
            className="bg-white/5 rounded-lg border border-white/10 p-4 flex items-center space-x-3 text-left hover:border-green-500/50 hover:bg-white/10 transition-colors cursor-pointer"
            data-testid="online-card-btn"
          >
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Circle className="h-4 w-4 text-green-500 fill-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="online-count">
                {users.filter(u => getRealtimeStatus(u).color === 'green').length}
              </p>
              <p className="text-xs text-gray-400">Online Now</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActivityGroup('recent')}
            className="bg-white/5 rounded-lg border border-white/10 p-4 flex items-center space-x-3 text-left hover:border-yellow-500/50 hover:bg-white/10 transition-colors cursor-pointer"
            data-testid="recently-active-card-btn"
          >
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="h-4 w-4 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="recently-active-count">
                {users.filter(u => {
                  const s = getRealtimeStatus(u);
                  return s.color === 'yellow' || s.color === 'orange';
                }).length}
              </p>
              <p className="text-xs text-gray-400">Recently Active</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActivityGroup('offline')}
            className="bg-white/5 rounded-lg border border-white/10 p-4 flex items-center space-x-3 text-left hover:border-white/30 hover:bg-white/10 transition-colors cursor-pointer"
            data-testid="offline-card-btn"
          >
            <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
              <Circle className="h-4 w-4 text-gray-500 fill-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="offline-count">
                {users.filter(u => getRealtimeStatus(u).color === 'gray').length}
              </p>
              <p className="text-xs text-gray-400">Offline</p>
            </div>
          </button>
        </div>

        <ActivityMembersDialog
          group={activityGroup}
          onClose={() => setActivityGroup(null)}
          users={users}
          getStatus={getRealtimeStatus}
        />

        {/* Search + Sort */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
          <Button
            onClick={() => { setSortByActivity(v => !v); setCurrentPage(1); }}
            variant="outline"
            className={`shrink-0 border-white/20 text-white hover:bg-white/10 ${sortByActivity ? 'bg-green-500/10 border-green-500/40 text-green-300' : ''}`}
            data-testid="sort-by-activity-btn"
            title={sortByActivity ? 'Sorted: Pending Approval → Online → Recently Active → Offline. Click to reset.' : 'Sort users by activity (pending approvals first, then online)'}
          >
            <Circle className={`h-3.5 w-3.5 mr-2 ${sortByActivity ? 'text-green-400 fill-green-400' : 'text-gray-400'}`} />
            Sort by activity
          </Button>
        </div>

        {/* Activity filter chips */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-xs text-gray-500 mr-1">Filter:</span>
          {[
            { key: null, label: 'All', dot: 'bg-white/40' },
            { key: 'online', label: 'Online', dot: 'bg-green-500' },
            { key: 'recent', label: 'Recently Active', dot: 'bg-yellow-500' },
            { key: 'offline', label: 'Long Inactive', dot: 'bg-gray-500' },
            { key: 'pending', label: 'Pending Approval', dot: 'bg-fuchsia-500' },
          ].map(({ key, label, dot }) => {
            const active = activityFilter === key;
            const count = key === null
              ? users.length
              : key === 'pending'
              ? users.filter(u => u.status === 'pending_admin_approval').length
              : users.filter(u => {
                  const c = getRealtimeStatus(u).color;
                  if (key === 'online') return c === 'green';
                  if (key === 'recent') return c === 'yellow' || c === 'orange';
                  return c === 'gray';
                }).length;
            return (
              <button
                key={label}
                type="button"
                onClick={() => { setActivityFilter(key); setCurrentPage(1); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-red-600/20 border-red-500/50 text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`activity-filter-${key || 'all'}`}
              >
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                {label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-red-500/30 text-red-200' : 'bg-white/10 text-gray-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
          {activityFilter && (
            <button
              type="button"
              onClick={() => { setActivityFilter(null); setCurrentPage(1); }}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors ml-1"
              data-testid="activity-filter-clear"
            >
              <XIcon className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              {searchTerm || activityFilter ? 'No users match your search or filter.' : 'No users yet. Click "Add User" to create one.'}
            </div>
          ) : (
            <table className="w-full min-w-[1100px]">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Activity</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Created</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Validity</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <UserAvatar
                            name={user.name}
                            avatarId={user.avatar_id}
                            avatarPhotoUrl={user.avatar_photo_url}
                            size="lg"
                            testId={`um-avatar-${user.id}`}
                          />
                          {/* Real-time online/offline indicator dot */}
                          {(() => {
                            const status = getRealtimeStatus(user);
                            const dotColor = status.color === 'green' ? 'bg-green-500' :
                                           status.color === 'yellow' ? 'bg-yellow-500' :
                                           status.color === 'orange' ? 'bg-orange-500' :
                                           status.color === 'fuchsia' ? 'bg-fuchsia-500' : 'bg-gray-500';
                            return (
                              <span 
                                className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-900 ${dotColor} ${status.color === 'green' ? 'animate-pulse' : ''}`}
                                title={status.text}
                                data-testid={`presence-dot-${user.id}`}
                              />
                            );
                          })()}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.role === 'admin' ? 'bg-purple-500/20 text-purple-300' :
                        user.role === 'author' ? 'bg-amber-500/20 text-amber-300' :
                        'bg-blue-500/20 text-blue-300'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center w-fit ${
                        user.status === 'active' ? 'bg-green-500/20 text-green-300' :
                        (user.status === 'pending_verification' || user.status === 'pending_email_verification') ? 'bg-yellow-500/20 text-yellow-300' :
                        user.status === 'pending_admin_approval' ? 'bg-fuchsia-500/20 text-fuchsia-300' :
                        user.status === 'inactive' ? 'bg-gray-500/20 text-gray-300' :
                        'bg-red-500/20 text-red-300'
                      }`}>
                        {user.status === 'active' ? <UserCheck className="h-3 w-3 mr-1" /> : <UserX className="h-3 w-3 mr-1" />}
                        {(user.status === 'pending_verification' || user.status === 'pending_email_verification') ? 'Pending Email'
                          : user.status === 'pending_admin_approval' ? 'Pending Approval'
                          : user.status || 'active'}
                      </span>
                      {(user.status === 'pending_verification' || user.status === 'pending_email_verification') && (
                        <div className="flex flex-col items-start gap-1 mt-2">
                          <button
                            onClick={() => handleResendVerification(user.id, user.name)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/25"
                            data-testid={`resend-verification-${user.id}`}
                          >
                            <Mail className="h-3 w-3" /> Resend email
                          </button>
                          <button
                            onClick={() => handleManualActivate(user.id, user.name, user.status)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30"
                            data-testid={`activate-user-${user.id}`}
                            title="Skip email verification and activate this account now"
                          >
                            <UserCheck className="h-3 w-3" /> Activate
                          </button>
                        </div>
                      )}
                      {user.status === 'pending_admin_approval' && (
                        <div className="mt-2">
                          <button
                            onClick={() => handleManualActivate(user.id, user.name, user.status)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-500/25 text-emerald-200 border border-emerald-500/50 hover:bg-emerald-500/35 shadow-sm"
                            data-testid={`activate-user-${user.id}`}
                            title="Approve this signup and grant immediate sign-in"
                          >
                            <UserCheck className="h-3 w-3" /> Approve &amp; activate
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const status = getRealtimeStatus(user);
                          const colorMap = {
                            green: { dot: 'text-green-500 fill-green-500', text: 'text-green-400', bg: 'bg-green-500' },
                            yellow: { dot: 'text-yellow-500 fill-yellow-500', text: 'text-yellow-400', bg: 'bg-yellow-500' },
                            orange: { dot: 'text-orange-500 fill-orange-500', text: 'text-orange-400', bg: 'bg-orange-500' },
                            fuchsia: { dot: 'text-fuchsia-500 fill-fuchsia-500', text: 'text-fuchsia-300', bg: 'bg-fuchsia-500' },
                            gray: { dot: 'text-gray-500 fill-gray-500', text: 'text-gray-400', bg: 'bg-gray-500' }
                          };
                          const c = colorMap[status.color] || colorMap.gray;
                          return (
                            <>
                              <Circle className={`h-2 w-2 ${c.dot}`} />
                              <span className={`text-sm font-medium ${c.text}`} data-testid={`activity-status-${user.id}`}>
                                {status.text}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                      {user.last_login && (
                        <p className="text-xs text-gray-500 mt-0.5 ml-4">
                          Login: {new Date(user.last_login).toLocaleString()}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(() => {
                        if (user.role === 'admin') {
                          return <span className="text-purple-300/70 text-xs italic">Lifetime (admin)</span>;
                        }
                        if (!user.valid_until) {
                          return (
                            <button
                              type="button"
                              onClick={() => setValidityModalUser(user)}
                              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white"
                              data-testid={`validity-set-${user.id}`}
                            >
                              <CalendarClock className="h-3 w-3" /> Lifetime — set expiry
                            </button>
                          );
                        }
                        const expiry = new Date(user.valid_until);
                        const expired = expiry < new Date();
                        return (
                          <button
                            type="button"
                            onClick={() => setValidityModalUser(user)}
                            className={`inline-flex items-center gap-1 text-xs font-medium hover:underline ${
                              expired ? 'text-red-300' : 'text-emerald-300'
                            }`}
                            data-testid={`validity-edit-${user.id}`}
                          >
                            <CalendarClock className="h-3 w-3" />
                            {expired ? 'Expired ' : 'Valid until '}
                            {expiry.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-1.5">
                        <button
                          onClick={() => openEditModal(user)}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10 hover:text-white"
                          title="Edit user details"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit2 className="h-3.5 w-3.5" /> Edit
                        </button>
                        {(user.role === 'author' || user.role === 'admin') && (
                          <button
                            onClick={() => setAssignBooksUser(user)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20"
                            title="Manually assign books to this author's My Books dashboard"
                            data-testid={`assign-books-${user.id}`}
                          >
                            <BookOpen className="h-3.5 w-3.5" /> Books
                          </button>
                        )}
                        <button
                          onClick={() => handleResetPassword(user.id, user.name)}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/20"
                          title="Reset this user's password to a new random one"
                          data-testid={`reset-password-${user.id}`}
                        >
                          <Key className="h-3.5 w-3.5" /> Reset
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500/25 hover:text-red-200"
                          title="Delete this user permanently"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between px-1" data-testid="user-pagination">
              <p className="text-xs text-gray-500">
                Showing {((currentPage - 1) * USERS_PER_PAGE) + 1}-{Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  data-testid="user-pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md cursor-pointer transition-colors ${
                      page === currentPage
                        ? 'bg-red-600 text-white'
                        : 'border border-white/10 text-gray-400 hover:bg-white/10'
                    }`}
                    data-testid={`user-pagination-page-${page}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  data-testid="user-pagination-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/10">
              {tempPassword ? (
                <div className="text-center">
                  <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <UserCheck className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">User Created!</h3>
                  <p className="text-gray-400 mb-2">Share this temporary password with the user:</p>
                  <div className="bg-black/50 rounded-lg p-4 mb-3 flex items-center justify-between">
                    <code className="text-yellow-400 font-mono">{tempPassword}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyPassword}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedPassword ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
                    <p className="text-yellow-300 text-sm font-medium">Verification email sent!</p>
                    <p className="text-yellow-200/60 text-xs mt-1">The user must click the link in their email to activate their account before logging in.</p>
                  </div>
                  <p className="text-gray-500 text-sm mb-4">The user can change their password after logging in.</p>
                  <Button
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-white mb-4">Create New User</h3>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label className="text-white">Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        required
                        data-testid="create-user-name"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({...formData, email: e.target.value});
                          validateEmailLive(e.target.value);
                        }}
                        className={`bg-white/5 border-white/10 text-white ${
                          emailValidation.status === 'valid' ? 'border-green-500/50' :
                          emailValidation.status === 'invalid' ? 'border-red-500/50' : ''
                        }`}
                        required
                        data-testid="create-user-email"
                      />
                      {emailValidation.status && (
                        <div className={`flex items-center gap-1.5 mt-1.5 text-xs ${
                          emailValidation.status === 'checking' ? 'text-zinc-400' :
                          emailValidation.status === 'valid' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {emailValidation.status === 'checking' && <Loader2 className="h-3 w-3 animate-spin" />}
                          {emailValidation.status === 'valid' && <CheckCircle className="h-3 w-3" />}
                          {emailValidation.status === 'invalid' && <AlertTriangle className="h-3 w-3" />}
                          <span>{emailValidation.message}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-white">Password (leave blank to auto-generate)</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="bg-white/5 border-white/10 text-white pr-10"
                          placeholder="Auto-generated if empty"
                          data-testid="create-user-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {formData.password && (
                        <div className="mt-2 space-y-1">
                          {getPasswordStrength(formData.password).checks.map((c) => (
                            <div key={`pwd-create-${c.label}`} className={`flex items-center gap-1.5 text-xs ${c.passed ? 'text-green-400' : 'text-zinc-500'}`}>
                              {c.passed ? <CheckCircle className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                              <span>{c.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">Role</Label>
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white"
                        >
                          <option value="member" className="bg-gray-900">Member</option>
                          <option value="author" className="bg-gray-900">Author</option>
                          <option value="admin" className="bg-gray-900">Admin</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-white">Status</Label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                          className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white"
                        >
                          <option value="active" className="bg-gray-900">Active</option>
                          <option value="inactive" className="bg-gray-900">Inactive</option>
                          <option value="suspended" className="bg-gray-900">Suspended</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-white">Notes (optional)</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Internal notes about this user"
                      />
                    </div>
                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setShowCreateModal(false); resetForm(); }}
                        className="flex-1 border-white/20 text-white hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                        Create User
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Edit User</h3>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <Label className="text-white">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">New Password (leave blank to keep current)</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Enter new password"
                    data-testid="edit-user-password"
                  />
                  {formData.password && (
                    <div className="mt-2 space-y-1">
                      {getPasswordStrength(formData.password).checks.map((c) => (
                        <div key={`pwd-edit-${c.label}`} className={`flex items-center gap-1.5 text-xs ${c.passed ? 'text-green-400' : 'text-zinc-500'}`}>
                          {c.passed ? <CheckCircle className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                          <span>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Role</Label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white"
                    >
                      <option value="member" className="bg-gray-900">Member</option>
                      <option value="author" className="bg-gray-900">Author</option>
                      <option value="admin" className="bg-gray-900">Admin</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">Status</Label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white"
                    >
                      <option value="active" className="bg-gray-900">Active</option>
                      <option value="inactive" className="bg-gray-900">Inactive</option>
                      <option value="suspended" className="bg-gray-900">Suspended</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-white">Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Internal notes about this user"
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowEditModal(false); resetForm(); }}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Reset Result Modal */}
        {tempPassword && !showCreateModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/10 text-center">
              <div className="bg-yellow-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Key className="h-8 w-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Password Reset</h3>
              <p className="text-gray-400 mb-4">New password for {selectedUser?.name}:</p>
              <div className="bg-black/50 rounded-lg p-4 mb-4 flex items-center justify-between">
                <code className="text-yellow-400 font-mono">{tempPassword}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyPassword}
                  className="text-gray-400 hover:text-white"
                >
                  {copiedPassword ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <Button
                onClick={() => { setTempPassword(null); setSelectedUser(null); }}
                className="bg-red-600 hover:bg-red-700"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Storage Modal */}
        {showStorage && storage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Database className="h-6 w-6 mr-2 text-blue-400" />
                  Storage Usage
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowStorage(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </Button>
              </div>

              {/* Usage Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Used: {storage.capacity.used_mb} MB</span>
                  <span className="text-gray-400">{storage.capacity.usage_percent}%</span>
                </div>
                <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      storage.capacity.usage_percent > 80 ? 'bg-red-500' : 
                      storage.capacity.usage_percent > 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storage.capacity.usage_percent, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {storage.capacity.remaining_mb} MB remaining of {storage.capacity.limit_mb} MB
                </p>
              </div>

              {/* Files Breakdown */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <Image className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{storage.files.covers.count}</p>
                  <p className="text-xs text-gray-400">Covers</p>
                  <p className="text-xs text-purple-400">{storage.files.covers.size_mb} MB</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <Book className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{storage.files.books.count}</p>
                  <p className="text-xs text-gray-400">Books</p>
                  <p className="text-xs text-blue-400">{storage.files.books.size_mb} MB</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <Headphones className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{storage.files.audiobooks.count}</p>
                  <p className="text-xs text-gray-400">Audio</p>
                  <p className="text-xs text-green-400">{storage.files.audiobooks.size_mb} MB</p>
                </div>
              </div>

              {/* Capacity Estimates */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-white mb-3">Remaining Capacity</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cover images (500KB avg)</span>
                    <span className="text-white">~{storage.estimates.remaining_covers} more</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Book PDFs (5MB avg)</span>
                    <span className="text-white">~{storage.estimates.remaining_books} more</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Audio chapters (10MB avg)</span>
                    <span className="text-white">~{storage.estimates.remaining_audiobook_chapters} more</span>
                  </div>
                </div>
              </div>

              {/* Records Count */}
              <div className="flex justify-between text-sm text-gray-400 border-t border-white/10 pt-4">
                <span>Total Books: {storage.records.books}</span>
                <span>Total Users: {storage.records.users}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== Validity modals (2026-05 trial feature) ===== */}
      {validityModalUser && (
        <ValidityEditorModal
          user={validityModalUser}
          onClose={() => setValidityModalUser(null)}
          onSaved={fetchUsers}
        />
      )}
      {/* ===== Manual book → author assignment modal (2026-06-08) ===== */}
      {assignBooksUser && (
        <AssignBooksModal
          user={assignBooksUser}
          onClose={() => setAssignBooksUser(null)}
          onChanged={fetchUsers}
        />
      )}
      {showBulkValidity && (
        <BulkValidityModal
          onClose={() => setShowBulkValidity(false)}
          onSaved={fetchUsers}
        />
      )}
    </div>
  );
};

export default UserManagement;
